import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CloseIcon from "@mui/icons-material/Close";

import {
  Box,
  Button,
  Checkbox,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";

import { Field, Form, Formik } from "formik";
import {
  CreateAdditionalOptionDto,
  CreateItemWithDetailDto,
} from "lib/features/item/types";
import Label from "../../components/Label";
import PostScript from "../../components/PostScript";
import priceStepValidation from "./PriceStep.schema";

import PercentageIcon from "assets/icons/percentage.svg";
import { useState } from "react";
import AdOnCheckBoxModal from "../../components/AdOnCheckBoxModal";
import AdOnDropdownModal from "../../components/AdOnDropdownModal";
import AdOnTextModal from "../../components/AdOnTextModal";

interface DetailsStepProp {
  initialValues: CreateItemWithDetailDto;
  onSubmit: (values: CreateItemWithDetailDto) => void;
  setFormValues: any;
}

const PriceStep = ({
  initialValues,
  onSubmit,
  setFormValues,
}: DetailsStepProp) => {
  const theme = useTheme();
  const [visibleCheckboxModal, setVisibleCheckboxModal] =
    useState<boolean>(false);
  const [visibleTextModal, setVisibleTextModal] = useState<boolean>(false);
  const [visibleDropdownModal, setVisibleDropdownModal] =
    useState<boolean>(false);

 const calculatePayoffPrice = (
  price: number,
  commissionRate: string | number
): string => {
  if (price < 1) {
    return 'Please set the price first.';
  }
  
  if (commissionRate === null || commissionRate === undefined || commissionRate === '') {
    return 'Please set a commission rate.';
  }

  const commissionPercentage =
    typeof commissionRate === 'string'
      ? parseFloat(commissionRate)
      : commissionRate;

  const platformFee = (price * 5) / 100;
  const commissionFee = (price * commissionPercentage) / 100;
  const payout = price - platformFee - commissionFee;

  return `$${payout.toFixed(2)}`;
};  

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={priceStepValidation}
      onSubmit={(values) => onSubmit(values)}
    >
      {({ values, errors, touched, setFieldValue }) => {
        const handleChange = (e: React.ChangeEvent<any>) => {
          const { name, value } = e.target;
          setFieldValue(name, value);
          setFormValues((prevValues: any) => ({
            ...prevValues,
            [name]: value,
          }));
        };

        const handleNumericChange = (
          e: React.ChangeEvent<any>,
          fieldName: string
        ) => {
          const { value } = e.target;
          if (/^\d*$/.test(value) || value === "") {
            const numericValue = value === "" ? null : Number(value);

            // Update Formik State
            setFieldValue(fieldName, numericValue);

            // Persist Data to Global State
            setFormValues((prevValues: any) => ({
              ...prevValues,
              [fieldName]: numericValue,
            }));
          }
        };

        const handleOptionSave = (data: {
          header: string;
          type: number; // 1: Text Field, 2: Dropdown, 3: Checkbox
          required: boolean;
          place_holder?: string;
          price?: number;
          availability?: number;
          description?: string;
        }) => {
          const newOption: CreateAdditionalOptionDto = {
            header: data.header,
            required: data.required,
            type: Number(data.type),
            place_holder: data.place_holder,
            description: data.description,
            price: Number(data.price),
            availability: Number(data.availability),
          };

          setFieldValue("additionalOptions", [
            ...(values.additionalOptions || []),
            newOption,
          ]);

          setFormValues((prevValues: any) => ({
            ...prevValues,
            additionalOptions: [
              ...(prevValues.additionalOptions || []),
              newOption,
            ],
          }));
        };

        const handleRemoveAdditionalOption = (index: number) => {
          if (values?.additionalOptions) {
            const updatedAdditionalOptions: CreateAdditionalOptionDto[] =
              values?.additionalOptions?.filter(
                (_: CreateAdditionalOptionDto, idx: number) => idx !== index
              );
            setFieldValue("additionalOptions", updatedAdditionalOptions);
            setFormValues((prevValues: any) => ({
              ...prevValues,
              additionalOptions: updatedAdditionalOptions,
            }));
          }
        };

        return (
          <Form>
            <Box sx={{ fontFamily: theme.typography.fontFamily }}>
              {/* Base Price and Availablity Container */}
              <Box sx={{ display: "flex", flexDirection: "row", gap: "10px" }}>
                {/* Base Price */}
                <Box sx={{ display: "flex", flex: 1, flexDirection: "column" }}>
                  <Label label="Base Price" required={true} />
                  <Field
                    as={TextField}
                    type="numeric"
                    name="price"
                    fullWidth
                    onChange={(e: any) => handleNumericChange(e, "price")}
                    error={touched.price && Boolean(errors.price)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">$</InputAdornment>
                      ),
                    }}
                  />

                  {errors.price && touched.price && (
                    <Typography color="red" fontSize="12px">
                      {errors.price}
                    </Typography>
                  )}
                </Box>

                {/* Available */}
                <Box sx={{ display: "flex", flex: 1, flexDirection: "column" }}>
                  <Label label="# Available" required={true} />
                  <Field
                    as={TextField}
                    type="numeric"
                    name="capacity"
                    fullWidth
                    onChange={(e: any) => handleNumericChange(e, "capacity")}
                    error={touched.capacity && Boolean(errors.capacity)}
                  />

                  {errors.capacity && touched.capacity && (
                    <Typography color="red" fontSize="12px">
                      {errors.capacity}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Pricing Description */}
              <Box>
                <Label label="Pricing Description" required={false} />
                <Field
                  as={TextField}
                  name="price_description"
                  fullWidth
                  onChange={handleChange}
                  error={
                    touched.price_description &&
                    Boolean(errors.price_description)
                  }
                />
                {errors.price_description && touched.price_description && (
                  <Typography color="red" fontSize="12px">
                    {errors.price_description}
                  </Typography>
                )}
                <PostScript
                  text="Explain your pricing model if you have variable pricing or add-ons."
                  italics="Ex: $1000 up to 10 people then an additional $100/person over. Ex: $100/single jet ski or $120/double jet ski"
                />
              </Box>

              {/* Unit Name */}
              <Box>
                <Label label="Unit Name" required={true} />
                <Field
                  as={TextField}
                  name="unit_name"
                  fullWidth
                  onChange={handleChange}
                  error={touched.unit_name && Boolean(errors.unit_name)}
                />
                {errors.unit_name && touched.unit_name && (
                  <Typography color="red" fontSize="12px">
                    {errors.unit_name}
                  </Typography>
                )}
                <PostScript italics="Ex: Boat, Jet skis, tickets, etc. " />
              </Box>

              {/* Commission Rate */}
              <Box>
                <Label label="Commission Rate" required={true} />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "center",
                    gap: "10px",
                  }}
                >
                  <Box sx={{ display: "flex", flex: 1 }}>
                    <Field
                      as={TextField}
                      name="comission"
                      fullWidth
                      onChange={(e: any) => handleNumericChange(e, "comission")}
                      error={touched.comission && Boolean(errors.comission)}
                    />
                  </Box>
                  <Box
                    sx={{
                      display: "flex",
                      // flex: 0.1,
                      width: "50px",
                      border: "1px solid black",
                      justifyContent: "center",
                      paddingY: "15px",
                      borderRadius: "5px",
                    }}
                  >
                    <img src={PercentageIcon} alt="age-icon" />
                  </Box>
                </Box>

                {errors.comission && touched.comission && (
                  <Typography color="red" fontSize="12px">
                    {errors.comission}
                  </Typography>
                )}
              </Box>

              {/* Payout box */}
              <Box
                sx={{
                  marginTop: "10px",
                  borderRadius: "8px",
                  backgroundColor: "#D0EED5",
                  padding: "10px",
                }}
              >
                <Typography
                  sx={{ color: "#1C1B1C", fontSize: "17px", fontWeight: "700" }}
                >
                  Your Pay Out Before Add-Ons:
                </Typography>
                <Typography
                  sx={{ color: "#1C1B1C", fontSize: "17px", fontWeight: "400" }}
                >
                  {calculatePayoffPrice(
                    Number(values.price),
                    Number(values.comission)
                  )}
                </Typography>
              </Box>

              {/* Customer Added Value */}
              <Box>
                <Label label="Customer Added Value" required={false} />
                <Field
                  as={TextField}
                  name="offering"
                  fullWidth
                  onChange={handleChange}
                  multiline
                  rows={3}
                  error={touched.offering && Boolean(errors.offering)}
                />
                {errors.offering && touched.offering && (
                  <Typography color="red" fontSize="12px">
                    {errors.offering}
                  </Typography>
                )}

                <PostScript
                  text="Great way to help encourage the booking."
                  italics="Ex: Free bottle of champagne or price discount explaining normal price and the now discounted price."
                />
              </Box>

              {/* Options and Add-Ons */}
              <Box>
                <Typography
                  sx={{
                    fontSize: "24px",
                    fontWeight: "700",
                    paddingTop: "20px",
                    paddingBottom: "10px",
                  }}
                >
                  Options and Add-Ons
                </Typography>
                <PostScript text="If you have add-ons or need more information from the client please select one of the option types below." />

                {/* Buttons Container */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "15px",
                    marginY: "10px",
                  }}
                >
                  <Box
                    sx={{ display: "flex", flexDirection: "row", gap: "10px" }}
                  >
                    <Button
                      sx={{
                        display: "flex",
                        flex: 1,
                        border: "1px solid black",
                        fontSize: "17px",
                        color: "#000",
                        fontWeight: "600",
                        boxShadow: "0 4px 4px rgba(0, 0, 0, 0.25)",
                        paddingY: "12px",
                      }}
                      onClick={() => setVisibleCheckboxModal(true)}
                    >
                      Check Box
                    </Button>
                    <Button
                      sx={{
                        display: "flex",
                        flex: 1,
                        border: "1px solid black",
                        fontSize: "17px",
                        color: "#000",
                        fontWeight: "600",
                        boxShadow: "0 4px 4px rgba(0, 0, 0, 0.25)",
                        paddingY: "12px",
                      }}
                      onClick={() => setVisibleTextModal(true)}
                    >
                      Text Field
                    </Button>
                  </Box>
                  <Box sx={{ width: "100%" }}>
                    <Button
                      sx={{
                        display: "flex",
                        flex: 1,
                        border: "1px solid black",
                        width: "100%",
                        fontSize: "17px",
                        color: "#000",
                        fontWeight: "600",
                        boxShadow: "0 4px 4px rgba(0, 0, 0, 0.25)",
                        paddingY: "12px",
                      }}
                      onClick={() => setVisibleDropdownModal(true)}
                    >
                      Quantity Dropdown
                    </Button>
                  </Box>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "row",
                    alignItems: "baseline",
                    gap: "2px",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#595959",
                      fontSize: "15px",
                      fontWeight: "700",
                    }}
                  >
                    *Note{" "}
                  </Typography>
                  <Typography
                    sx={{
                      color: "#595959",
                      fontSize: "15px",
                      fontWeight: "700",
                    }}
                  >
                    -
                  </Typography>
                  <PostScript italics=" Every checkout link collects customer name, email, phone, group type and optional comments." />
                </Box>
              </Box>

              {/* Additional Information */}
              <Box>
                {values?.additionalOptions &&
                  values?.additionalOptions?.length > 0 &&
                  values?.additionalOptions?.map(
                    (item: CreateAdditionalOptionDto, index: any) => (
                      <Box
                        key={index}
                        sx={{
                          border: "1px solid #595959",
                          // gap: "7px",
                          padding: "10px",
                          borderRadius: "8px",
                          marginY: "10px",
                        }}
                      >
                        {/* Header */}
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "row",
                            justifyContent: "space-between",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "17px",
                              color: "#000",
                              fontWeight: "700",
                            }}
                          >
                            {item.type === 1
                              ? "Text Field"
                              : item.type === 2
                              ? "Quantity Dropdown"
                              : "Check Box"}
                          </Typography>
                          <IconButton
                            onClick={() => {
                              handleRemoveAdditionalOption(index);
                            }}
                          >
                            <CloseIcon />
                          </IconButton>
                        </Box>

                        {/* Body */}
                        {item?.type === 1 ? (
                          <>
                            <Typography
                              sx={{
                                marginTop: "0px",
                                fontSize: "17px",
                                fontWeight: "600",
                                paddingY: "5px",
                              }}
                            >
                              {item?.header}
                              {item?.required && "*"}
                            </Typography>
                            <Field
                              as={TextField}
                              name="place_holder"
                              placeholder={item?.place_holder ?? ""}
                              fullWidth
                            />
                          </>
                        ) : item?.type === 2 ? (
                          <>
                            <Typography
                              sx={{
                                marginTop: "0px",
                                fontSize: "17px",
                                fontWeight: "600",
                                paddingY: "5px",
                              }}
                            >
                              {item?.header}
                              {item?.required && "*"}
                            </Typography>

                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "row",
                                alignItems: "center",
                                border: "1px solid #000",
                                borderRadius: "8px",
                                padding: "15px",
                                justifyContent: "space-between",
                              }}
                            >
                              <Typography
                                sx={{
                                  fontSize: "16px",
                                  color: "#000",
                                  fontWeight: "400",
                                }}
                              >
                                {item?.availability}
                              </Typography>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  gap: "20px",
                                  alignItems: "center",
                                }}
                              >
                                {/* Price */}
                                <Typography
                                  sx={{
                                    fontSize: "16px",
                                    color: "#000",
                                    fontWeight: "600",
                                  }}
                                >
                                  ${item?.price}
                                </Typography>

                                <ArrowDropDownIcon />
                              </Box>
                            </Box>
                          </>
                        ) : (
                          <Box
                            sx={{
                              border: "1px solid #E0E0E0",
                              borderRadius: "8px",
                              padding: "10px",
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              marginBottom: "10px",
                            }}
                          >
                            {/* Left side: Checkbox */}
                            <Box sx={{ marginRight: "10px" }}>
                              <Checkbox name="isRequired" />
                            </Box>

                            {/* Middle section: Content */}
                            <Box sx={{ flex: 1 }}>
                              <Box
                                sx={{
                                  display: "flex",
                                  flexDirection: "row",
                                  justifyContent: "space-between",
                                }}
                              >
                                <Typography
                                  sx={{
                                    marginTop: "0px",
                                    fontSize: "17px",
                                    fontWeight: "600",
                                  }}
                                >
                                  {item?.header}
                                  {item?.required && "*"}
                                </Typography>
                                <Typography
                                  sx={{
                                    marginTop: "0px",
                                    fontSize: "17px",
                                    fontWeight: "700",
                                  }}
                                >
                                  ${item?.price}
                                </Typography>
                              </Box>

                              <Typography
                                sx={{
                                  fontSize: "15px",
                                  color: "#595959",
                                  marginTop: "0px",
                                  fontWeight: "400",
                                }}
                              >
                                {item?.description}
                              </Typography>
                            </Box>
                          </Box>
                        )}
                      </Box>
                    )
                  )}
              </Box>

              <Button
                type="submit"
                variant="contained"
                sx={{ width: "100%", marginTop: "20px", paddingY: "10px" }}
              >
                Continue
              </Button>
            </Box>

            {/* AdOnCheckBoxModal */}
            <AdOnCheckBoxModal
              open={visibleCheckboxModal}
              handleClose={() => setVisibleCheckboxModal(false)}
              setFormValues={setFormValues}
              onSave={handleOptionSave}
            />

            {/* AdOnCheckBoxModal */}
            <AdOnTextModal
              open={visibleTextModal}
              handleClose={() => setVisibleTextModal(false)}
              setFormValues={setFormValues}
              onSave={handleOptionSave}
            />

            {/* AdOnDropdownModal */}
            <AdOnDropdownModal
              open={visibleDropdownModal}
              handleClose={() => setVisibleDropdownModal(false)}
              setFormValues={setFormValues}
              onSave={handleOptionSave}
            />
          </Form>
        );
      }}
    </Formik>
  );
};

export default PriceStep;
